// src/MyTripLogs.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapPin, User, Layers } from 'lucide-react';

// Leaflet marker icon fix (CRA/webpack)
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function pad2(n) {
  return String(n).padStart(2, '0');
}

// Fallback only (used if /server-time fails)
function todayYMDLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Robust time formatting.
 * - If serverTz exists AND ts is parseable as Date => format using Intl in that org TZ.
 * - Otherwise: fall back to extracting HH:mm from string (works for MySQL DATETIME strings).
 */
function fmtTimeOrg(ts, serverTz) {
  if (!ts) return '—';
  const s = String(ts);

  // quick path: if already "HH:mm" or contains it
  const m = s.match(/(\d{2}):(\d{2})/);
  const fallback = m ? `${m[1]}:${m[2]}` : s.slice(11, 16);

  if (!serverTz) return fallback;

  // try parse as Date
  try {
    // normalize common MySQL format "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ss"
    let iso = s;
    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(iso)) iso = iso.replace(' ', 'T');

    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return fallback;

    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: serverTz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return dtf.format(d);
  } catch {
    return fallback;
  }
}

function fmtDuration(sec) {
  if (sec == null) return '—';
  const s = Number(sec);
  if (!Number.isFinite(s) || s < 0) return '—';
  const h = Math.floor(s / 3600);
  const min = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${min} min`;
  return `${min} min`;
}

function fmtMilesFromMeters(m) {
  if (m == null) return '—';
  const mi = Number(m) / 1609.344;
  return `${mi.toFixed(2)} mi`;
}

function isYMD(d) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(d || '').slice(0, 10));
}

export default function MyTripLogs() {
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const role = String(currentUser?.role || '');
  const isAdmin = role === 'admin' || role === 'super_admin';
  const selfTeacherId = currentUser?.id;

  // ✅ Server/org date + timezone (authoritative for "Today")
  const [serverDateStr, setServerDateStr] = useState(null);
  const [serverTz, setServerTz] = useState(null);

  // Start empty; set after /server-time
  const [date, setDate] = useState('');

  const [teachers, setTeachers] = useState([]);
  const [teacherId, setTeacherId] = useState(isAdmin ? '' : String(selfTeacherId || ''));

  const [loadingTrips, setLoadingTrips] = useState(false);
  const [trips, setTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(null);

  const [loadingTrip, setLoadingTrip] = useState(false);
  const [tripDetails, setTripDetails] = useState(null);

  // ✅ Whole-day (“combined”) view state
  const [viewMode, setViewMode] = useState('single'); // 'single' | 'day'
  const [loadingDay, setLoadingDay] = useState(false);
  const [dayTripsDetails, setDayTripsDetails] = useState([]);
  const [dayLoadError, setDayLoadError] = useState('');

  // ✅ Fetch org-local "today" from server
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get('/server-time');
        if (cancelled) return;

        const st = String(res.data?.serverTime || ''); // "YYYY-MM-DDTHH:mm:ss"
        const d = st.slice(0, 10);

        if (isYMD(d)) {
          setServerDateStr(d);
          setServerTz(res.data?.timezone || null);
          setDate((prev) => (prev ? prev : d));
        } else {
          const local = todayYMDLocal();
          setServerDateStr(local);
          setDate((prev) => (prev ? prev : local));
        }
      } catch {
        const local = todayYMDLocal();
        if (cancelled) return;
        setServerDateStr(local);
        setDate((prev) => (prev ? prev : local));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Load teachers (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await API.get('/admin/teachers/min');
        setTeachers(res.data?.teachers || []);
      } catch {
        setTeachers([]);
      }
    })();
  }, [isAdmin]);

  const loadTrips = async (dateStr) => {
    const d = String(dateStr || '').slice(0, 10);
    if (!isYMD(d)) return;

    setLoadingTrips(true);
    setTrips([]);
    setSelectedTripId(null);
    setTripDetails(null);

    // reset day view data on date/teacher change
    setDayTripsDetails([]);
    setDayLoadError('');
    setViewMode('single');

    try {
      const params = { date: d };
      if (isAdmin && teacherId) params.teacher_id = teacherId;
      const res = await API.get('/travel/trips', { params });

      // ✅ Ensure trips list is chronological (first trip at top -> last at bottom)
      const list = Array.isArray(res.data?.trips) ? res.data.trips : [];
      const sorted = [...list].sort((a, b) => {
        const ta = new Date(String(a?.started_at || '').includes('T') ? a.started_at : String(a?.started_at || '').replace(' ', 'T')).getTime();
        const tb = new Date(String(b?.started_at || '').includes('T') ? b.started_at : String(b?.started_at || '').replace(' ', 'T')).getTime();
        if (!Number.isFinite(ta) || !Number.isFinite(tb)) {
          return String(a?.started_at || '').localeCompare(String(b?.started_at || ''));
        }
        return ta - tb; // ASC
      });

      setTrips(sorted);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to load trips.');
    } finally {
      setLoadingTrips(false);
    }
  };

  useEffect(() => {
    if (!date) return;
    loadTrips(date);
    // eslint-disable-next-line
  }, [date, teacherId]);

  const loadTripDetails = async (tripId) => {
    if (!tripId) return;
    setSelectedTripId(tripId);
    setLoadingTrip(true);
    setTripDetails(null);

    try {
      const res = await API.get(`/travel/trips/${tripId}`);
      setTripDetails(res.data?.trip || null);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to load trip details.');
    } finally {
      setLoadingTrip(false);
    }
  };

  // ✅ Load all trip details for the selected day (combined view)
  const loadDayTripsDetails = async () => {
    if (!Array.isArray(trips) || trips.length === 0) {
      setDayTripsDetails([]);
      setDayLoadError('No trips for selected date.');
      return;
    }

    setLoadingDay(true);
    setDayTripsDetails([]);
    setDayLoadError('');

    try {
      const details = [];
      const maxTrips = 40;

      // ✅ use already-sorted trips (ASC)
      const slice = trips.slice(0, maxTrips);

      for (const t of slice) {
        const id = t?.trip_id;
        if (!id) continue;
        // eslint-disable-next-line no-await-in-loop
        const res = await API.get(`/travel/trips/${id}`);
        const trip = res.data?.trip || null;
        if (trip) details.push(trip);
      }

      // ✅ ensure details are also chronological (ASC)
      details.sort((a, b) => {
        const ta = new Date(String(a?.started_at || '').includes('T') ? a.started_at : String(a?.started_at || '').replace(' ', 'T')).getTime();
        const tb = new Date(String(b?.started_at || '').includes('T') ? b.started_at : String(b?.started_at || '').replace(' ', 'T')).getTime();
        if (!Number.isFinite(ta) || !Number.isFinite(tb)) {
          return String(a?.started_at || '').localeCompare(String(b?.started_at || ''));
        }
        return ta - tb;
      });

      if (trips.length > maxTrips) {
        setDayLoadError(`Showing first ${maxTrips} trips only (too many trips for one day).`);
      }

      setDayTripsDetails(details);
    } catch (e) {
      console.error(e);
      setDayLoadError(e?.response?.data?.error || 'Failed to load day trips.');
    } finally {
      setLoadingDay(false);
    }
  };

  // When switching to day view, load details if missing
  useEffect(() => {
    if (viewMode !== 'day') return;
    if (dayTripsDetails.length) return;
    if (loadingDay) return;
    loadDayTripsDetails();
    // eslint-disable-next-line
  }, [viewMode]);

  // ============ Single trip polyline ============
  const polyline = useMemo(() => {
    const pts = tripDetails?.points || [];
    return pts
      .filter((p) => p.lat != null && p.lon != null)
      .map((p) => [Number(p.lat), Number(p.lon)]);
  }, [tripDetails]);

  const startMarker = useMemo(() => (polyline.length ? polyline[0] : null), [polyline]);
  const endMarker = useMemo(() => (polyline.length ? polyline[polyline.length - 1] : null), [polyline]);

  const destMarker = useMemo(() => {
    const d = tripDetails?.destination;
    if (d?.lat != null && d?.lon != null) return [Number(d.lat), Number(d.lon)];
    return null;
  }, [tripDetails]);

  const mapCenterSingle = useMemo(() => {
    if (destMarker) return destMarker;
    if (endMarker) return endMarker;
    if (startMarker) return startMarker;
    return [32.7157, -117.1611];
  }, [destMarker, startMarker, endMarker]);

  // ============ Day (combined) polylines ============
  const dayPolylines = useMemo(() => {
    const all = [];
    for (const td of dayTripsDetails || []) {
      const pts = td?.points || [];
      const line = pts
        .filter((p) => p.lat != null && p.lon != null)
        .map((p) => [Number(p.lat), Number(p.lon)]);
      if (line.length >= 2) {
        all.push({
          trip_id: td.trip_id,
          line,
          destination: td.destination,
          started_at: td.started_at,
          stopped_at: td.stopped_at,
          distance_m: td.distance_m,
          duration_sec: td.duration_sec,
          points_count: td.points_count,
        });
      }
    }

    // ✅ ensure day polylines are chronological
    all.sort((a, b) => {
      const ta = new Date(String(a?.started_at || '').includes('T') ? a.started_at : String(a?.started_at || '').replace(' ', 'T')).getTime();
      const tb = new Date(String(b?.started_at || '').includes('T') ? b.started_at : String(b?.started_at || '').replace(' ', 'T')).getTime();
      if (!Number.isFinite(ta) || !Number.isFinite(tb)) {
        return String(a?.started_at || '').localeCompare(String(b?.started_at || ''));
      }
      return ta - tb;
    });

    return all;
  }, [dayTripsDetails]);

  const dayFirst = useMemo(() => (dayPolylines.length ? dayPolylines[0]?.line?.[0] : null), [dayPolylines]);
  const dayLast = useMemo(() => {
    if (!dayPolylines.length) return null;
    const lastLine = dayPolylines[dayPolylines.length - 1]?.line || [];
    return lastLine.length ? lastLine[lastLine.length - 1] : null;
  }, [dayPolylines]);

  const mapCenterDay = useMemo(() => {
    if (dayLast) return dayLast;
    if (dayFirst) return dayFirst;
    return [32.7157, -117.1611];
  }, [dayFirst, dayLast]);

  const dayTotals = useMemo(() => {
    const totalMeters = (dayTripsDetails || []).reduce((a, t) => a + (Number(t?.distance_m || 0) || 0), 0);
    const totalSec = (dayTripsDetails || []).reduce((a, t) => a + (Number(t?.duration_sec || 0) || 0), 0);
    const totalPts = (dayTripsDetails || []).reduce((a, t) => a + (Number(t?.points_count || 0) || 0), 0);
    return { totalMeters, totalSec, totalPts, tripsCount: (dayTripsDetails || []).length };
  }, [dayTripsDetails]);

  const todayServer = serverDateStr || todayYMDLocal();
  const rightTitle = viewMode === 'day' ? 'Day Route' : 'Trip Details';

  return (
    <div className="w-full px-2 sm:px-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-3">
        <div className="min-w-0">
          <h2 className="text-lg font-bold">Trip Logs</h2>
          <div className="text-xs text-slate-500">
            Trips are recorded only during Travel mode.
            {serverTz ? (
              <span className="ml-2">
                Timezone: <span className="font-semibold">{serverTz}</span>
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          {isAdmin ? (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-500" />
              <select
                className="border rounded px-3 py-2 text-sm"
                value={teacherId}
                onChange={(e) => setTeacherId(e.target.value)}
              >
                <option value="">-- Select teacher --</option>
                {teachers.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name || t.email || `#${t.id}`}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-600">Date</span>
            <input
              type="date"
              className="border rounded px-3 py-2 text-sm"
              value={date || ''}
              onChange={(e) => setDate(e.target.value)}
            />
            <button
              onClick={() => setDate(todayServer)}
              className="px-3 py-2 text-xs rounded border bg-white hover:bg-slate-50"
              disabled={!todayServer}
              title={serverTz ? `Today in ${serverTz}` : 'Today'}
            >
              Today
            </button>
          </div>
        </div>
      </div>

      {/* View mode toggle */}
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setViewMode('single')}
          className={`px-3 py-2 text-xs rounded border ${
            viewMode === 'single' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white hover:bg-slate-50'
          }`}
        >
          Single trip
        </button>
        <button
          type="button"
          onClick={() => setViewMode('day')}
          className={`px-3 py-2 text-xs rounded border inline-flex items-center gap-2 ${
            viewMode === 'day' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white hover:bg-slate-50'
          }`}
          disabled={!trips.length}
          title={!trips.length ? 'No trips to combine' : 'Show all trips on one map'}
        >
          <Layers className="w-4 h-4" />
          Whole day
        </button>

        {viewMode === 'day' ? (
          <button
            type="button"
            onClick={() => loadDayTripsDetails()}
            className="ml-auto px-3 py-2 text-xs rounded border bg-white hover:bg-slate-50"
            disabled={loadingDay || !trips.length}
            title="Reload all trip paths for the day"
          >
            {loadingDay ? 'Loading…' : 'Reload day path'}
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* LEFT */}
        <div className="lg:col-span-1 border rounded-xl bg-white">
          <div className="px-3 py-2 border-b bg-slate-50 rounded-t-xl flex items-center justify-between">
            <div className="font-semibold text-sm">Trips</div>
            <div className="text-xs text-slate-500">{loadingTrips ? 'Loading…' : `${trips.length}`}</div>
          </div>

          {!date ? (
            <div className="p-4 text-sm text-slate-500">Loading date…</div>
          ) : loadingTrips ? (
            <div className="p-4 text-sm text-slate-500">Loading trips…</div>
          ) : trips.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">No trips for selected date.</div>
          ) : (
            <div className="divide-y">
              {trips.map((t) => {
                const selected = Number(selectedTripId) === Number(t.trip_id);
                const openTrip = !t.stopped_at;

                return (
                  <button
                    key={t.trip_id}
                    onClick={() => {
                      setViewMode('single');
                      loadTripDetails(t.trip_id);
                    }}
                    className={`w-full text-left px-3 py-3 hover:bg-slate-50 ${selected ? 'bg-indigo-50' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {t.dest_label || `Trip #${t.trip_id}`}
                        </div>

                        {t.dest_address_line ? (
                          <div className="text-[11px] text-slate-500 mt-0.5 truncate">{t.dest_address_line}</div>
                        ) : null}

                        <div className="text-[11px] text-slate-500 mt-1">
                          {fmtTimeOrg(t.started_at, serverTz)} → {t.stopped_at ? fmtTimeOrg(t.stopped_at, serverTz) : '—'} •{' '}
                          {fmtDuration(t.duration_sec)}
                          {openTrip ? <span className="ml-2 text-amber-700 font-semibold">open</span> : null}
                        </div>

                        {t.checkin_time ? (
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Check-in:{' '}
                            <span className="font-semibold">{fmtTimeOrg(t.checkin_time, serverTz)}</span>
                            {t.time_to_checkin_sec != null ? (
                              <span className="ml-2 text-slate-600">
                                Wait: <span className="font-semibold">{fmtDuration(t.time_to_checkin_sec)}</span>
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>

                      <div className="shrink-0 flex flex-col items-end gap-1">
                        <span className="text-[11px] px-2 py-1 rounded-full border bg-white text-slate-700">
                          {t.points_count || 0} pts
                        </span>
                        <span className="text-[11px] px-2 py-1 rounded-full border bg-white text-slate-700">
                          {fmtMilesFromMeters(t.distance_m)}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-2 border rounded-xl bg-white overflow-hidden">
          <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between">
            <div className="font-semibold text-sm">{rightTitle}</div>

            {viewMode === 'day' ? (
              <div className="text-xs text-slate-600">
                Trips: <span className="font-semibold">{dayTotals.tripsCount}</span>
                <span className="mx-2">•</span>
                Distance:{' '}
                <span className="font-semibold">{fmtMilesFromMeters(dayTotals.totalMeters)}</span>
                <span className="mx-2">•</span>
                Duration:{' '}
                <span className="font-semibold">{fmtDuration(dayTotals.totalSec)}</span>
              </div>
            ) : tripDetails?.distance_m != null ? (
              <div className="text-xs text-slate-600">
                Distance: <span className="font-semibold">{fmtMilesFromMeters(tripDetails.distance_m)}</span>
              </div>
            ) : (
              <div className="text-xs text-slate-500">{loadingTrip ? 'Loading…' : ''}</div>
            )}
          </div>

          {/* ===== WHOLE DAY VIEW ===== */}
          {viewMode === 'day' ? (
            !date ? (
              <div className="p-4 text-sm text-slate-500">Loading date…</div>
            ) : loadingDay ? (
              <div className="p-4 text-sm text-slate-500">Loading day path…</div>
            ) : dayLoadError ? (
              <div className="p-4 text-sm text-amber-800 bg-amber-50 border-t">{dayLoadError}</div>
            ) : dayPolylines.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No path data for selected date.</div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 p-3">
                {/* Map (whole day) */}
                <div className="xl:col-span-2 border rounded-xl overflow-hidden">
                  <div className="h-[360px]">
                    <MapContainer center={mapCenterDay} zoom={12} style={{ height: '100%', width: '100%' }}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                      {dayPolylines.map((t) => (
                        <Polyline key={`day_line_${t.trip_id}`} positions={t.line} />
                      ))}

                      {dayFirst ? (
                        <Marker position={dayFirst}>
                          <Popup>Day start</Popup>
                        </Marker>
                      ) : null}

                      {dayLast ? (
                        <Marker position={dayLast}>
                          <Popup>Day last point</Popup>
                        </Marker>
                      ) : null}

                      {dayTripsDetails
                        .filter((td) => td?.destination?.lat != null && td?.destination?.lon != null)
                        .slice(0, 20)
                        .map((td) => (
                          <Marker
                            key={`day_dest_${td.trip_id}`}
                            position={[Number(td.destination.lat), Number(td.destination.lon)]}
                          >
                            <Popup>
                              <div className="text-sm font-semibold">
                                {td.destination?.label || `Trip #${td.trip_id}`}
                              </div>
                              {td.destination?.address_line ? (
                                <div className="text-xs text-slate-600 mt-1">{td.destination.address_line}</div>
                              ) : null}
                              <div className="text-[11px] text-slate-500 mt-1">
                                {fmtTimeOrg(td.started_at, serverTz)} →{' '}
                                {td.stopped_at ? fmtTimeOrg(td.stopped_at, serverTz) : '—'}
                              </div>
                            </Popup>
                          </Marker>
                        ))}
                    </MapContainer>
                  </div>
                </div>

                {/* Day stats / list */}
                <div className="xl:col-span-1 border rounded-xl p-3">
                  <div className="text-sm font-semibold text-slate-900">Day summary</div>
                  <div className="text-xs text-slate-600 mt-2">
                    Date: <span className="font-semibold">{date}</span>
                  </div>
                  {serverTz ? (
                    <div className="text-xs text-slate-600 mt-1">
                      Timezone: <span className="font-semibold">{serverTz}</span>
                    </div>
                  ) : null}

                  <div className="text-xs text-slate-600 mt-2">
                    Trips: <span className="font-semibold">{dayTotals.tripsCount}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    Total distance: <span className="font-semibold">{fmtMilesFromMeters(dayTotals.totalMeters)}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    Total duration: <span className="font-semibold">{fmtDuration(dayTotals.totalSec)}</span>
                  </div>

                  <div className="mt-3">
                    <div className="text-xs font-semibold text-slate-700">Trips in order</div>
                    <div className="mt-2 space-y-2 max-h-[210px] overflow-y-auto pr-1">
                      {dayTripsDetails.map((td) => (
                        <button
                          key={`day_trip_${td.trip_id}`}
                          type="button"
                          onClick={() => {
                            setViewMode('single');
                            loadTripDetails(td.trip_id);
                          }}
                          className="w-full text-left text-[11px] border rounded p-2 bg-white hover:bg-slate-50"
                          title="Open this trip"
                        >
                          <div className="font-semibold text-slate-800 truncate">
                            {td.destination?.label || td.dest_label || `Trip #${td.trip_id}`}
                          </div>
                          <div className="text-slate-500 mt-0.5">
                            {fmtTimeOrg(td.started_at, serverTz)} → {td.stopped_at ? fmtTimeOrg(td.stopped_at, serverTz) : '—'}
                            <span className="mx-2">•</span>
                            {fmtMilesFromMeters(td.distance_m)}
                            <span className="mx-2">•</span>
                            {fmtDuration(td.duration_sec)}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-3 text-[11px] text-slate-500">
                    Tip: “Whole day” draws every trip on one map so you can see the full route.
                  </div>
                </div>
              </div>
            )
          ) : (
            /* ===== SINGLE TRIP VIEW ===== */
            !selectedTripId ? (
              <div className="p-4 text-sm text-slate-500">Select a trip to view details.</div>
            ) : loadingTrip ? (
              <div className="p-4 text-sm text-slate-500">Loading trip details…</div>
            ) : !tripDetails ? (
              <div className="p-4 text-sm text-slate-500">No data.</div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 p-3">
                {/* Map */}
                <div className="xl:col-span-2 border rounded-xl overflow-hidden">
                  <div className="h-[360px]">
                    <MapContainer center={mapCenterSingle} zoom={13} style={{ height: '100%', width: '100%' }}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      {polyline.length > 1 ? <Polyline positions={polyline} /> : null}

                      {startMarker ? (
                        <Marker position={startMarker}>
                          <Popup>Start</Popup>
                        </Marker>
                      ) : null}

                      {endMarker ? (
                        <Marker position={endMarker}>
                          <Popup>Last point</Popup>
                        </Marker>
                      ) : null}

                      {destMarker ? (
                        <Marker position={destMarker}>
                          <Popup>
                            <div className="text-sm font-semibold">{tripDetails.destination?.label || 'Destination'}</div>
                            {tripDetails.destination?.address_line ? (
                              <div className="text-xs text-slate-600 mt-1">{tripDetails.destination.address_line}</div>
                            ) : null}
                          </Popup>
                        </Marker>
                      ) : null}
                    </MapContainer>
                  </div>
                </div>

                {/* Timeline / stats */}
                <div className="xl:col-span-1 border rounded-xl p-3">
                  <div className="text-sm font-semibold text-slate-900">Trip #{tripDetails.trip_id}</div>

                  {tripDetails.destination?.label ? (
                    <div className="text-xs text-slate-700 mt-1 font-semibold">{tripDetails.destination.label}</div>
                  ) : null}
                  {tripDetails.destination?.address_line ? (
                    <div className="text-[11px] text-slate-500 mt-1">{tripDetails.destination.address_line}</div>
                  ) : null}

                  <div className="text-xs text-slate-600 mt-2">
                    {fmtTimeOrg(tripDetails.started_at, serverTz)} →{' '}
                    {tripDetails.stopped_at ? fmtTimeOrg(tripDetails.stopped_at, serverTz) : '—'}
                  </div>

                  <div className="text-xs text-slate-600 mt-1">
                    Duration: <span className="font-semibold">{fmtDuration(tripDetails.duration_sec)}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    Points: <span className="font-semibold">{tripDetails.points_count}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    Distance: <span className="font-semibold">{fmtMilesFromMeters(tripDetails.distance_m)}</span>
                  </div>

                  {Array.isArray(tripDetails.stops) && tripDetails.stops.length ? (
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-slate-700">Detected stops</div>
                      <div className="mt-2 space-y-2">
                        {tripDetails.stops.slice(0, 6).map((s, idx) => (
                          <div key={idx} className="text-[11px] border rounded p-2 bg-slate-50">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-slate-700">Stop</span>
                              <span className="text-slate-600">{fmtDuration(s.duration_sec)}</span>
                            </div>
                            {s.lat != null && s.lon != null ? (
                              <div className="mt-1 text-slate-600 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {Number(s.lat).toFixed(5)}, {Number(s.lon).toFixed(5)}
                              </div>
                            ) : null}
                          </div>
                        ))}
                        {tripDetails.stops.length > 6 ? (
                          <div className="text-[11px] text-slate-500">+ {tripDetails.stops.length - 6} more…</div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 text-[11px] text-slate-500">No stops detected (or not enough data).</div>
                  )}

                  <div className="mt-3 text-[11px] text-slate-500">
                    Tip: Switch to “Whole day” to see all trips drawn together.
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <div className="mt-3 text-[11px] text-slate-500">
        Note: Times shown here are formatted in the organization timezone when possible (using /server-time timezone). If other modules show UTC,
        the backend should return org-local DATETIME strings (recommended) or ISO with timezone that we can format consistently.
      </div>
    </div>
  );
}




